import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PiPaletteLight, PiPlusLight, PiChartBarLight, PiChartLineLight } from 'react-icons/pi';

export type ChartTypeOverride = 'default' | 'bar' | 'line';

export interface ColorTheme {
  name: string;
  primary: string;
  palette: string[];
}

export const COLOR_THEMES: Record<string, ColorTheme> = {
  blue: {
    name: 'Blue',
    primary: '#1E3A8A',
    palette: ['#1E3A8A', '#2563EB', '#3B82F6', '#60A5FA', '#93C5FD', '#BFDBFE', '#DBEAFE', '#EFF6FF'],
  },
  indigo: {
    name: 'Indigo',
    primary: '#312E81',
    palette: ['#312E81', '#4338CA', '#6366F1', '#818CF8', '#A5B4FC', '#C7D2FE', '#E0E7FF', '#EEF2FF'],
  },
  purple: {
    name: 'Purple',
    primary: '#581C87',
    palette: ['#581C87', '#7C3AED', '#8B5CF6', '#A78BFA', '#C4B5FD', '#DDD6FE', '#EDE9FE', '#F5F3FF'],
  },
  teal: {
    name: 'Teal',
    primary: '#134E4A',
    palette: ['#134E4A', '#0D9488', '#14B8A6', '#2DD4BF', '#5EEAD4', '#99F6E4', '#CCFBF1', '#F0FDFA'],
  },
  emerald: {
    name: 'Emerald',
    primary: '#064E3B',
    palette: ['#064E3B', '#059669', '#10B981', '#34D399', '#6EE7B7', '#A7F3D0', '#D1FAE5', '#ECFDF5'],
  },
  rose: {
    name: 'Rose',
    primary: '#881337',
    palette: ['#881337', '#E11D48', '#F43F5E', '#FB7185', '#FDA4AF', '#FECDD3', '#FFE4E6', '#FFF1F2'],
  },
  amber: {
    name: 'Amber',
    primary: '#78350F',
    palette: ['#78350F', '#D97706', '#F59E0B', '#FBBF24', '#FCD34D', '#FDE68A', '#FEF3C7', '#FFFBEB'],
  },
  slate: {
    name: 'Slate',
    primary: '#1E293B',
    palette: ['#1E293B', '#475569', '#64748B', '#94A3B8', '#CBD5E1', '#E2E8F0', '#F1F5F9', '#F8FAFC'],
  },
};

const STORAGE_KEY = 'analytics-color-theme';
const CUSTOM_COLOR_KEY = 'analytics-custom-color';

/** Generate an 8-step palette from a single hex color */
const generatePalette = (hex: string): string[] => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return Array.from({ length: 8 }, (_, i) => {
    const mix = i / 7; // 0 (darkest) → 1 (lightest)
    const nr = Math.round(r + (255 - r) * mix * 0.85);
    const ng = Math.round(g + (255 - g) * mix * 0.85);
    const nb = Math.round(b + (255 - b) * mix * 0.85);
    return `#${nr.toString(16).padStart(2, '0')}${ng.toString(16).padStart(2, '0')}${nb.toString(16).padStart(2, '0')}`;
  });
};

export const getStoredCustomColor = (): string => {
  try { return localStorage.getItem(CUSTOM_COLOR_KEY) || '#1E3A8A'; } catch { return '#1E3A8A'; }
};

export const setStoredCustomColor = (hex: string): void => {
  try { localStorage.setItem(CUSTOM_COLOR_KEY, hex); } catch {}
};

const CHART_TYPE_KEY = 'analytics-chart-type';

export const getStoredChartType = (): ChartTypeOverride => {
  try { return (localStorage.getItem(CHART_TYPE_KEY) as ChartTypeOverride) || 'default'; } catch { return 'default'; }
};

export const setStoredChartType = (t: ChartTypeOverride): void => {
  try { localStorage.setItem(CHART_TYPE_KEY, t); } catch {}
};

export const getStoredTheme = (): string => {
  try {
    return localStorage.getItem(STORAGE_KEY) || 'blue';
  } catch {
    return 'blue';
  }
};

export const setStoredTheme = (key: string): void => {
  try {
    localStorage.setItem(STORAGE_KEY, key);
  } catch {
    // localStorage unavailable
  }
};

export const getActiveColorTheme = (key?: string): ColorTheme => {
  const k = key || getStoredTheme();
  if (k === 'custom') {
    const hex = getStoredCustomColor();
    return { name: 'Custom', primary: hex, palette: generatePalette(hex) };
  }
  return COLOR_THEMES[k] || COLOR_THEMES.blue;
};

interface ColorCustomizerProps {
  activeTheme: string;
  onThemeChange: (themeKey: string) => void;
  chartType?: ChartTypeOverride;
  onChartTypeChange?: (t: ChartTypeOverride) => void;
}

const ColorCustomizer: React.FC<ColorCustomizerProps> = ({ activeTheme, onThemeChange, chartType = 'default', onChartTypeChange }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [customColor, setCustomColor] = useState(() => getStoredCustomColor());
  const popoverRef = useRef<HTMLDivElement>(null);
  const customPalette = useMemo(() => generatePalette(customColor), [customColor]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={popoverRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-gray-200 bg-white/70 hover:bg-white text-gray-600 hover:text-gray-800 transition-all duration-200 dark:bg-gray-800/70 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
      >
        <PiPaletteLight className="w-3.5 h-3.5" />
        Customize View
      </button>

      {isOpen && (
        <div className="glass absolute right-0 top-full mt-2 w-64 rounded-lg shadow-lg z-50 p-3 animate-fade-in">
          <p className="text-[11px] font-medium text-gray-400 dark:text-gray-500 tracking-wider mb-2.5">
            Color theme
          </p>
          <div className="space-y-0.5">
            {Object.entries(COLOR_THEMES).map(([key, theme]) => (
              <button
                key={key}
                onClick={() => {
                  onThemeChange(key);
                  setIsOpen(false);
                }}
                className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md text-xs font-medium transition-all duration-150 ${
                  activeTheme === key
                    ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                }`}
              >
                <div className="flex gap-0.5 flex-shrink-0">
                  {theme.palette.slice(0, 5).map((color, i) => (
                    <div
                      key={i}
                      className="w-3.5 h-3.5 rounded-sm"
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
                <span>{theme.name}</span>
                {activeTheme === key && (
                  <svg className="ml-auto w-3.5 h-3.5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </button>
            ))}
          </div>

          {/* Chart type toggle */}
          <div className="mt-2.5 pt-2.5 border-t border-gray-100 dark:border-gray-700">
            <p className="text-[11px] font-medium text-gray-400 dark:text-gray-500 tracking-wider mb-2">
              Chart style
            </p>
            <div className="flex gap-1">
              {([['default', 'Auto'], ['bar', 'Bar'], ['line', 'Line']] as [ChartTypeOverride, string][]).map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => {
                    onChartTypeChange?.(key);
                    setStoredChartType(key);
                  }}
                  className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 text-[11px] font-medium rounded-md transition-all duration-150 ${
                    chartType === key
                      ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 ring-1 ring-gray-200 dark:ring-gray-600'
                      : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                  }`}
                >
                  {key === 'bar' && <PiChartBarLight className="w-3.5 h-3.5" />}
                  {key === 'line' && <PiChartLineLight className="w-3.5 h-3.5" />}
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Custom color picker */}
          <div className="mt-2.5 pt-2.5 border-t border-gray-100 dark:border-gray-700">
            <button
              onClick={() => {
                setStoredCustomColor(customColor);
                onThemeChange('custom');
                setIsOpen(false);
              }}
              className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md text-xs font-medium transition-all duration-150 ${
                activeTheme === 'custom'
                  ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50'
              }`}
            >
              <div className="flex gap-0.5 flex-shrink-0">
                {customPalette.slice(0, 5).map((color, i) => (
                  <div key={i} className="w-3.5 h-3.5 rounded-sm" style={{ backgroundColor: color }} />
                ))}
              </div>
              <span>{t('Custom')}</span>
              {activeTheme === 'custom' && (
                <svg className="ml-auto w-3.5 h-3.5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              )}
            </button>
            <div className="flex items-center gap-2 mt-2 px-2.5">
              <input
                type="color"
                value={customColor}
                onChange={(e) => {
                  setCustomColor(e.target.value);
                  setStoredCustomColor(e.target.value);
                  if (activeTheme === 'custom') onThemeChange('custom');
                }}
                className="w-7 h-7 rounded cursor-pointer border border-gray-200 dark:border-gray-600 p-0"
              />
              <input
                type="text"
                value={customColor}
                onChange={(e) => {
                  const v = e.target.value;
                  if (/^#[0-9A-Fa-f]{6}$/.test(v)) {
                    setCustomColor(v);
                    setStoredCustomColor(v);
                    if (activeTheme === 'custom') onThemeChange('custom');
                  } else {
                    setCustomColor(v);
                  }
                }}
                className="flex-1 px-2 py-1 text-[11px] font-mono border border-gray-200 dark:border-gray-600 rounded bg-gray-50 dark:bg-gray-900 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-1 focus:ring-info-300"
                placeholder="#1E3A8A"
                maxLength={7}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ColorCustomizer;
